import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Home, Loader2 } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const pollCountRef = useRef(0);

  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setTransaction(response.data);
      if (response.data.payment_status === 'paid') {
        setStatus('paid');
      } else if (pollCountRef.current < 15) {
        pollCountRef.current += 1;
        setTimeout(checkStatus, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) { setStatus('error'); }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    checkStatus();
  }, [sessionId, checkStatus]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <Card className="max-w-md w-full p-10 border border-border shadow-sm text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-6" />
            <h1 className="text-2xl font-medium mb-3">Confirming Payment...</h1>
            <p className="text-muted-foreground">Please wait while we verify your transaction.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium mb-3" data-testid="payment-success-title">Payment Successful!</h1>
            <p className="text-muted-foreground mb-2">Your booking for <strong>{transaction?.property_title}</strong> is confirmed.</p>
            <p className="text-2xl font-bold text-primary mb-8">${transaction?.amount?.toFixed(2)}</p>
            <div className="space-y-3">
              {transaction?.lease_id && (
                <Button data-testid="view-lease-btn" onClick={() => navigate(`/leases/${transaction.lease_id}`)}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">View Lease</Button>
              )}
              <Button data-testid="back-home-btn" onClick={() => navigate('/')} variant="outline" className="w-full rounded-full">
                <Home className="w-4 h-4 mr-2" />Back to Home
              </Button>
            </div>
          </>
        )}
        {(status === 'error' || status === 'timeout') && (
          <>
            <h1 className="text-2xl font-medium mb-3">Verification Pending</h1>
            <p className="text-muted-foreground mb-6">Your payment is being processed. Please check your leases page in a few moments.</p>
            <Button onClick={() => navigate('/leases')} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
              View My Leases
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
